import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactElement,
} from 'react';
import { useForm, type FieldNamesMarkedBoolean } from 'react-hook-form';
import { Loader2, Save, X, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PolicyModeSelector } from '@/components/policylist/components/PolicyModeSelector';
import { ArrayObjectSetting } from '@/components/policylist/components/ArrayObjectSetting';
import { PatternArrayModalField } from '@/components/policylist/components/PatternArrayModalField';
import { BetaBadge } from '@/components/policylist/components/BetaBadge';
import {
  MODE_SUB_POLICY_ENFORCE_TOOLTIP,
  MODE_SUB_POLICY_NOTE,
  modeSubPolicyDisabledNote,
  narrowPolicyMode,
} from '@/components/policylist/constants';
import {
  shouldUsePatternModal,
  getPatternModalDescription,
  hasBetaBadge,
} from '@/components/policylist/helpers/patternModalRegistry';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import {
  usePolicyEditContext,
  useTogglePolicyVersioned,
  useUpdatePolicySettingsVersioned,
  useResetPolicySettings,
  type PolicyEditScope,
} from '@/hooks/api/usePolicyEditContext';
import {
  PolicyMode,
  type PolicyEditContext,
  type PolicyModeType,
  type PolicySetting,
} from '@kirin/api-client/contracts/policies';
import { useToast } from '@/hooks/use-toast';
import { useIsSupport } from '@/hooks/useRoleCheck';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';
import { isAllowBlockPolicyKey } from '@/components/policylist/allow-block/config';
import { buildPolicySettingsPayload } from '@/components/policylist/allow-block/lib/buildPolicySettingsPayload';
import { useUserPolicySettings } from '@/hooks/api/useUserPolicies';
import { useOrganizationPolicySettings } from '@/hooks/api/useOrganizationPolicies';
import { usePolicySettingVisibility } from '@/components/policylist/components/usePolicySettingVisibility';
import { isSupersededPolicySetting } from '@/components/policylist/components/policySettingsVisibility';
import {
  policySettingsToFormData,
  type PolicySettingsFormData,
} from '@/components/policylist/types';
import { getAlertSubPolicyRelatedSettings } from '@kirin/api-client/data/alert-sub-policy-settings';
import { RelatedPolicySettings } from './RelatedPolicySettings';
import { getAlertSubPolicyExtras } from './alertSubPolicyExtras';

/** Stored pattern shape (legacy `reason`, newer `description`). */
interface StoredPattern {
  pattern: string;
  reason?: string;
  description?: string;
  severity: SeverityType;
}

/**
 * Build a minimal PolicySetting from the edit context subPolicy data.
 * The edit-context API doesn't return `validation`, so we infer property
 * structure from the array's first item (or defaultValue).
 */
function buildSettingFromEditContext(
  subPolicy: NonNullable<PolicyEditContext['subPolicy']>
): PolicySetting {
  const sampleItem =
    (Array.isArray(subPolicy.value) ? subPolicy.value[0] : null) ??
    (Array.isArray(subPolicy.defaultValue)
      ? subPolicy.defaultValue[0]
      : null) ??
    {};

  const properties: Record<
    string,
    { type: string; required?: boolean; enum?: string[] }
  > = {};
  for (const key of Object.keys(sampleItem)) {
    if (key === 'severity') {
      properties[key] = {
        type: 'string',
        required: true,
        enum: ['low', 'medium', 'high', 'critical'],
      };
    } else {
      properties[key] = { type: 'string', required: true };
    }
  }

  return {
    key: subPolicy.key,
    name: subPolicy.name,
    description: subPolicy.description,
    type: 'array',
    defaultValue: subPolicy.defaultValue,
    value: subPolicy.value,
    isCustomized: subPolicy.isCustomized,
    validation: {
      arrayItemType: 'object',
      required: false,
      properties,
    },
  };
}

/** Copy for the discard prompt shown when closing or leaving a dirty popup. */
const DISCARD_PROMPT = {
  title: 'Discard unsaved changes?',
  body: 'You have unsaved changes. If you leave now, your changes will be lost.',
  cancelLabel: 'Stay on Policy',
  discardLabel: 'Leave Policy',
} as const;

/** True when a react-hook-form dirty-field entry marks any change. */
function isDirtyEntry(entry: unknown): boolean {
  if (entry === true) return true;
  if (Array.isArray(entry)) return entry.some(isDirtyEntry);
  if (entry !== null && typeof entry === 'object') {
    return Object.values(entry).some(isDirtyEntry);
  }
  return false;
}

/** Top-level setting keys the user changed in the related-settings form. */
function dirtySettingKeys(
  dirtyFields: Partial<
    Readonly<FieldNamesMarkedBoolean<PolicySettingsFormData>>
  >
): string[] {
  return Object.entries(dirtyFields)
    .filter(([, entry]) => isDirtyEntry(entry))
    .map(([key]) => key);
}

interface EditPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: PolicyEditScope;
  policyKey: string;
  subPolicyKey?: string;
  alertKey?: string;
}

export function EditPolicyDialog({
  open,
  onOpenChange,
  scope,
  policyKey,
  subPolicyKey,
  alertKey,
}: EditPolicyDialogProps): ReactElement | null {
  const { toast } = useToast();
  const { changeScopeTo } = useScopeChangeAction();
  const { markDirty, markClean } = useUnsavedChangeActions();
  const scopeId = `edit-policy-${policyKey}`;

  // Account scope is version-less, so optimistic-locking conflict handling
  // only applies to organization-scoped edits.
  const supportsConflict = scope.kind === 'organization';
  const isAccountScope = scope.kind === 'account';
  const organizationId =
    scope.kind === 'organization' ? scope.organizationId : undefined;

  // KIR-4929: the settings the Policies page shows next to the alert's
  // sub-policy (e.g. the built-in secret detection rules beside "Secret
  // Rules"), rendered below the sub-policy control.
  const relatedSettingKeys = useMemo(
    () => getAlertSubPolicyRelatedSettings(policyKey, subPolicyKey),
    [policyKey, subPolicyKey]
  );
  const hasRelatedSettings = relatedSettingKeys.length > 0;
  const { flags: visibilityFlags, shouldHideSetting } =
    usePolicySettingVisibility(policyKey);
  // Non-setting rows (links to Policies sub-pages) for this sub-policy.
  const relatedExtras = useMemo(
    () => getAlertSubPolicyExtras(policyKey, subPolicyKey, visibilityFlags),
    [policyKey, subPolicyKey, visibilityFlags]
  );

  // useIsSupport(undefined) resolves hasAccess:true (no rolesConfig => "any
  // authenticated user"), so we only trust it when scope is org-scoped.
  const { isSupport } = useIsSupport(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );
  const isSupportReadOnly = scope.kind === 'organization' && isSupport;

  const {
    data: editContext,
    isLoading,
    isError,
    refetch,
  } = usePolicyEditContext(scope, policyKey, subPolicyKey, open);

  const {
    data: accountPolicySettings,
    isLoading: accountSettingsLoading,
    isFetching: accountSettingsFetching,
    isError: accountSettingsError,
    refetch: refetchAccountPolicySettings,
  } = useUserPolicySettings(
    policyKey,
    // Only popups that can write settings need the stored card; a mode-only
    // popup (no sub-policy, no related rows) never reads it.
    open &&
      isAccountScope &&
      (isAllowBlockPolicyKey(policyKey) ||
        !!subPolicyKey ||
        hasRelatedSettings),
    {
      // Account saves replace the full settings row (`upsertUserPolicySettings`
      // deletes the policy's stored settings, then inserts the payload), so every
      // account save sends the whole card — hydrate from server on each open.
      refetchOnMount: 'always',
    }
  );

  // Organization saves merge partial payloads; the full settings are needed
  // only to render the related rows.
  const {
    data: organizationPolicySettings,
    isLoading: organizationSettingsLoading,
    isError: organizationSettingsError,
    refetch: refetchOrganizationPolicySettings,
  } = useOrganizationPolicySettings(
    organizationId,
    policyKey,
    open && hasRelatedSettings
  );

  const policySettingsData = isAccountScope
    ? accountPolicySettings
    : organizationPolicySettings;
  const relatedSettingsLoading = isAccountScope
    ? accountSettingsLoading
    : organizationSettingsLoading;

  const relatedForm = useForm<PolicySettingsFormData>({
    defaultValues: {},
    mode: 'onChange',
  });
  const { reset: resetRelatedForm } = relatedForm;
  // Read during render so react-hook-form subscribes to both.
  const { isDirty: relatedDirty, dirtyFields: relatedDirtyFields } =
    relatedForm.formState;

  // Hydrate from the server values whenever they (re)load. The query polls and
  // refetches on focus, so a background refresh must not wipe the user's
  // unsaved edits: keep dirty rows and update only the untouched ones. Save
  // and discard reset the form explicitly.
  useEffect(() => {
    if (policySettingsData) {
      resetRelatedForm(policySettingsToFormData(policySettingsData), {
        keepDirtyValues: true,
      });
    }
  }, [policySettingsData, resetRelatedForm]);

  const toggleMutation = useTogglePolicyVersioned(scope);
  const settingsMutation = useUpdatePolicySettingsVersioned(scope);

  const [mode, setMode] = useState<PolicyModeType>(PolicyMode.Off);
  const [subPolicyValue, setSubPolicyValue] = useState<unknown>(null);
  const [subPolicyBaseline, setSubPolicyBaseline] = useState<unknown>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);

  // The related keys this policy actually declares, in registry order. The
  // sub-policy's own key is excluded — its control is rendered above.
  const relatedSettings = useMemo(
    () =>
      relatedSettingKeys.flatMap(key => {
        const setting = policySettingsData?.settings.find(s => s.key === key);
        return setting && setting.key !== editContext?.subPolicy?.key
          ? [setting]
          : [];
      }),
    [relatedSettingKeys, policySettingsData, editContext?.subPolicy?.key]
  );

  // Mode / sub-policy edits live in `hasChanges`; related rows in the form.
  const hasUnsavedChanges = hasChanges || relatedDirty;

  // Reset local state when the server version changes. This uses the
  // "setState during render" pattern recommended by React docs for syncing
  // derived state — avoids useEffect cascading renders.
  const [syncedVersion, setSyncedVersion] = useState<number | null>(null);
  if (editContext && editContext.version !== syncedVersion) {
    setSyncedVersion(editContext.version);
    setMode(editContext.policy.mode);
    if (editContext.subPolicy) {
      setSubPolicyValue(editContext.subPolicy.value);
      // Baseline snapshot drives the pattern modal's "New/Edited/Deleted"
      // chips against the server value at open time.
      setSubPolicyBaseline(editContext.subPolicy.value);
    }
    setHasChanges(false);
    setHasConflict(false);
  }

  useEffect(() => {
    if (hasUnsavedChanges) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [hasUnsavedChanges, markDirty, markClean, scopeId]);

  /** Check whether the sub-policy value has been modified from the server state */
  const isSubPolicyChanged = useCallback(
    (newValue: unknown): boolean => {
      const serverValue = editContext?.subPolicy?.value;
      if (serverValue === undefined || serverValue === null) {
        return false;
      }
      if (typeof newValue === 'boolean' || typeof serverValue === 'boolean') {
        return newValue !== serverValue;
      }
      // Deep comparison for arrays/objects
      return JSON.stringify(newValue) !== JSON.stringify(serverValue);
    },
    [editContext?.subPolicy?.value]
  );

  const handleModeChange = useCallback(
    (newMode: PolicyModeType): void => {
      setMode(newMode);
      setHasChanges(
        newMode !== editContext?.policy.mode ||
          isSubPolicyChanged(subPolicyValue)
      );
    },
    [editContext, subPolicyValue, isSubPolicyChanged]
  );

  const handleSubPolicyChange = useCallback(
    (newValue: unknown): void => {
      setSubPolicyValue(newValue);
      setHasChanges(
        mode !== editContext?.policy.mode || isSubPolicyChanged(newValue)
      );
    },
    [editContext, mode, isSubPolicyChanged]
  );

  const handleSave = useCallback(async (): Promise<void> => {
    if (!editContext) return;

    const version = editContext.version;
    const modeChanged = mode !== editContext.policy.mode;
    const subPolicyChanged =
      !!editContext.subPolicy && isSubPolicyChanged(subPolicyValue);
    const relatedValues = relatedForm.getValues();
    const changedRelatedKeys = dirtySettingKeys(relatedDirtyFields);
    const settingChanged = subPolicyChanged || changedRelatedKeys.length > 0;
    const usesBlockedListMode = isAllowBlockPolicyKey(policyKey);

    // Same per-row validation the Policies page runs on submit.
    // Only the changed rows: they are visible, so a failure shows inline. An
    // untouched (possibly flag-hidden) row must not block the save unseen.
    if (
      changedRelatedKeys.length > 0 &&
      !(await relatedForm.trigger(changedRelatedKeys))
    ) {
      return;
    }

    if (
      isAccountScope &&
      (settingChanged || (usesBlockedListMode && modeChanged)) &&
      (accountSettingsFetching || accountPolicySettings === undefined)
    ) {
      toast({
        title: 'Failed to update policy',
        description: accountSettingsError
          ? 'Could not load current policy settings. Please try again.'
          : 'Policy settings are still loading. Please wait and try again.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const updates: Record<string, unknown> = {};
      for (const key of changedRelatedKeys) {
        updates[key] = relatedValues[key];
      }
      if (subPolicyChanged && editContext.subPolicy) {
        updates[editContext.subPolicy.key] = subPolicyValue;
      }

      // Organization saves merge partial payloads. Account saves replace the
      // whole row, so they resend every stored value exactly as read — not the
      // Policies form's view of it, which folds cursor-database's legacy
      // `credentialPatterns` into `maliciousPatterns` — with this save's
      // changes on top.
      const buildSettingsPayload = (
        settings: Record<string, unknown>
      ): Record<string, unknown> =>
        isAccountScope
          ? buildPolicySettingsPayload(accountPolicySettings, settings)
          : settings;

      if (usesBlockedListMode && modeChanged) {
        // Allow/block policies store user-facing mode in `blockedListMode`
        // (policy settings), not scanner_prefs — mirror AllowBlockPolicySettingsForm.
        await settingsMutation.mutateAsync({
          settingsUpdate: {
            policyKey,
            settings: buildSettingsPayload({
              blockedListMode: mode,
              ...updates,
            }),
          } as Parameters<
            typeof settingsMutation.mutateAsync
          >[0]['settingsUpdate'],
          expectedVersion: version,
          alertKey,
        });
      } else {
        if (modeChanged) {
          const enabled = mode !== PolicyMode.Off;
          await toggleMutation.mutateAsync({
            policyKey,
            enabled,
            mode,
            expectedVersion: version,
            alertKey,
          });
        }

        if (settingChanged) {
          // If mode was toggled first, the server bumped the combined version by 1.
          // Pass the incremented version so the settings call doesn't 409.
          const settingsVersion = modeChanged ? version + 1 : version;
          await settingsMutation.mutateAsync({
            settingsUpdate: {
              policyKey,
              settings: buildSettingsPayload(updates),
            } as Parameters<
              typeof settingsMutation.mutateAsync
            >[0]['settingsUpdate'],
            expectedVersion: settingsVersion,
            alertKey,
          });
        }
      }

      toast({
        title: 'Policy updated successfully.',
      });
      setHasChanges(false);
      resetRelatedForm(relatedValues);
      markClean(scopeId);
      onOpenChange(false);
    } catch (error: unknown) {
      const status =
        error != null &&
        typeof error === 'object' &&
        'status' in error &&
        typeof (error as { status: unknown }).status === 'number'
          ? (error as { status: number }).status
          : undefined;

      if (status === 409) {
        setHasConflict(true);
        if (hasRelatedSettings && !isAccountScope) {
          // Unlike a background refresh, a conflict reload drops the related
          // edits too, as the version sync does for the mode and sub-policy:
          // a retry must not resend a value written over a change never seen.
          // Drop them now and reload the settings before the edit context:
          // its new version clears the conflict and turns Save back on.
          resetRelatedForm();
          const { data } = await refetchOrganizationPolicySettings();
          if (data) {
            resetRelatedForm(policySettingsToFormData(data));
          }
        }
        void refetch();
      } else {
        const reason =
          error != null &&
          typeof error === 'object' &&
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : 'An unexpected error occurred.';
        toast({
          title: 'Failed to update policy',
          description: reason,
          variant: 'destructive',
        });
      }
    }
  }, [
    editContext,
    mode,
    subPolicyValue,
    policyKey,
    alertKey,
    toggleMutation,
    settingsMutation,
    toast,
    onOpenChange,
    refetch,
    markClean,
    scopeId,
    isSubPolicyChanged,
    isAccountScope,
    relatedForm,
    relatedDirtyFields,
    resetRelatedForm,
    hasRelatedSettings,
    refetchOrganizationPolicySettings,
    accountPolicySettings,
    accountSettingsError,
    accountSettingsFetching,
  ]);

  const isSaving = toggleMutation.isPending || settingsMutation.isPending;

  const resetMutation = useResetPolicySettings(scope);

  const handleResetToDefaults = useCallback(async (): Promise<void> => {
    if (!editContext?.subPolicy) return;
    try {
      await resetMutation.mutateAsync({
        policyKey,
        settingKey: editContext.subPolicy.key,
      });
      toast({
        title: 'Policy updated successfully.',
      });
      void refetch();
    } catch {
      toast({
        title: 'Failed to update policy',
        description: 'Could not reset settings to defaults.',
        variant: 'destructive',
      });
    }
  }, [editContext, policyKey, resetMutation, toast, refetch]);

  const isResetting = resetMutation.isPending;
  const isBusy = isSaving || isResetting;

  const resetToServerState = useCallback((): void => {
    if (editContext) {
      setMode(editContext.policy.mode);
      if (editContext.subPolicy) {
        setSubPolicyValue(editContext.subPolicy.value);
      }
      setHasChanges(false);
    }
    if (policySettingsData) {
      resetRelatedForm(policySettingsToFormData(policySettingsData));
    }
    markClean(scopeId);
  }, [editContext, policySettingsData, resetRelatedForm, markClean, scopeId]);

  const closeDialog = useCallback((): void => {
    resetToServerState();
    onOpenChange(false);
  }, [resetToServerState, onOpenChange]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      if (isBusy) return;
      if (!hasUnsavedChanges) {
        closeDialog();
        return;
      }
      changeScopeTo({
        ...DISCARD_PROMPT,
        from: scopeId,
        id: `close-${scopeId}`,
        run: closeDialog,
      });
    },
    [
      closeDialog,
      onOpenChange,
      isBusy,
      hasUnsavedChanges,
      changeScopeTo,
      scopeId,
    ]
  );

  // Leaving through a related row's link (e.g. to the secret detection rules
  // editor) asks the same question as closing the popup.
  const confirmDiscard = useCallback(
    (proceed: () => void): void => {
      changeScopeTo({
        ...DISCARD_PROMPT,
        from: scopeId,
        id: `leave-${scopeId}`,
        run: () => {
          closeDialog();
          proceed();
        },
      });
    },
    [changeScopeTo, scopeId, closeDialog]
  );

  // The alert's own control, unless a newer engine supersedes that setting —
  // e.g. the legacy Dangerous Permissions list under capability detection,
  // which the scanner then no longer reads. Only superseded settings: a row
  // the Policies page hides because its feature flag is off stays, since the
  // alert itself shows the feature ran (and browser flags can lag the org's).
  const shownSubPolicy =
    editContext?.subPolicy &&
    !isSupersededPolicySetting(editContext.subPolicy.key, visibilityFlags)
      ? editContext.subPolicy
      : null;

  // Build the header: "Edit Policy: PolicyName > SubPolicyName" or "Edit Policy: PolicyName"
  const headerTitle = editContext
    ? shownSubPolicy
      ? `Edit Policy: ${editContext.policy.name} > ${shownSubPolicy.name}`
      : `Edit Policy: ${editContext.policy.name}`
    : 'Edit Policy';

  const showResetButton = shownSubPolicy?.isCustomized ?? false;

  const isArraySubPolicy = shownSubPolicy?.type === 'array';
  const usesPatternModal =
    isArraySubPolicy &&
    !!shownSubPolicy &&
    shouldUsePatternModal(policyKey, shownSubPolicy.key);

  // Beta chip parity with the Policies screen — driven by the shared registry
  // so Alerts and Policies never disagree on which sub-policies are Beta.
  const showSubPolicyBeta =
    !!shownSubPolicy && hasBetaBadge(policyKey, shownSubPolicy.key);

  const hasRelatedRows = relatedSettings.length > 0 || relatedExtras.length > 0;

  // Account saves replace the whole row, so a popup that can write settings
  // needs the stored card first. A mode-only popup never writes settings.
  const accountSaveNeedsSettings =
    isAccountScope &&
    (isAllowBlockPolicyKey(policyKey) ||
      !!editContext?.subPolicy ||
      hasRelatedSettings);

  const accountSettingsHydrating =
    accountSaveNeedsSettings &&
    (accountSettingsLoading || accountSettingsFetching);

  const accountSettingsUnavailable =
    accountSaveNeedsSettings &&
    !accountSettingsHydrating &&
    (accountSettingsError || accountPolicySettings === undefined);

  // A save writes settings only for a changed setting, or for an allow/block
  // policy's mode (stored as `blockedListMode`). A plain mode change is a
  // toggle call, so the stored card never holds it back.
  const pendingSettingsWrite =
    relatedDirty ||
    (!!editContext?.subPolicy && isSubPolicyChanged(subPolicyValue)) ||
    (isAllowBlockPolicyKey(policyKey) &&
      !!editContext &&
      mode !== editContext.policy.mode);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={
          isArraySubPolicy || hasRelatedSettings || relatedExtras.length > 0
            ? 'sm:max-w-3xl max-h-[90vh] overflow-y-auto'
            : 'sm:max-w-lg'
        }
        onInteractOutside={e => e.preventDefault()}
        onOverlayPointerDown={() => handleOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle>{headerTitle}</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="py-4 text-sm text-destructive">
            Failed to load policy context. Please try again.
          </div>
        )}

        {editContext && !isLoading && (
          <div className="space-y-4">
            {/* Policy card — name, description, mode selector */}
            <div className="rounded-md border p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">
                  {editContext.policy.name}
                </Label>
                <PolicyModeSelector
                  value={mode}
                  onChange={handleModeChange}
                  disabled={isBusy || isSupportReadOnly}
                  supportsEnforce={editContext.policy.supportsEnforce}
                  enforceDisabledReason={
                    editContext.policy.enforceDisabledReason
                  }
                  enforceTooltip={editContext.policy.enforceTooltip}
                  variant="contrast"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {editContext.policy.description}
              </p>
            </div>

            {/* Sub-policy toggle (boolean settings) */}
            {shownSubPolicy && shownSubPolicy.type === 'boolean' && (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label className="text-sm font-medium">
                    {shownSubPolicy.name}
                  </Label>
                  {showSubPolicyBeta && <BetaBadge />}
                  {shownSubPolicy.description && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help text-muted-foreground text-xs">
                          &#9432;
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {shownSubPolicy.description}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={subPolicyValue === true}
                    onCheckedChange={handleSubPolicyChange}
                    disabled={isBusy || mode === PolicyMode.Off}
                  />
                  <span className="text-sm text-muted-foreground">
                    {subPolicyValue ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            )}

            {/* Sub-policy mode selector (KIR-4426 nested mode sub-policies,
                e.g. Real-Time Tool-Call Analysis). Without this branch a
                `mode`-typed sub-policy matched neither the boolean nor the
                array case and the dialog rendered no sub-policy control at
                all. Disabled when the parent mode is Off for the same reason
                as the boolean switch above: the parent is a master switch, so
                the stored sub-mode is not in effect. `mode` is local dialog
                state, so flipping the selector above greys this one out
                immediately — no refetch. The displayed Off is cosmetic:
                `subPolicyValue` is untouched, so closing without a change
                never writes Off over the stored mode. */}
            {shownSubPolicy && shownSubPolicy.type === 'mode' && (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label className="text-sm font-medium">
                    {shownSubPolicy.name}
                  </Label>
                  {showSubPolicyBeta && <BetaBadge />}
                  {shownSubPolicy.description && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help text-muted-foreground text-xs">
                          &#9432;
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {shownSubPolicy.description}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <PolicyModeSelector
                  value={
                    mode === PolicyMode.Off
                      ? PolicyMode.Off
                      : narrowPolicyMode(subPolicyValue)
                  }
                  onChange={handleSubPolicyChange}
                  disabled={
                    isBusy || isSupportReadOnly || mode === PolicyMode.Off
                  }
                  supportsEnforce={editContext.policy.supportsEnforce}
                  enforceDisabledReason={
                    editContext.policy.enforceDisabledReason
                  }
                  enforceTooltip={MODE_SUB_POLICY_ENFORCE_TOOLTIP}
                  variant="contrast"
                  size="sm"
                />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {mode === PolicyMode.Off
                    ? // `subPolicyValue` is the PENDING value: an edit made
                      // before the parent was switched Off is still what Save
                      // would write, so the note names it rather than leaving
                      // a mode the admin can no longer see waiting to resume.
                      modeSubPolicyDisabledNote(
                        editContext.policy.name,
                        narrowPolicyMode(subPolicyValue)
                      )
                    : MODE_SUB_POLICY_NOTE}
                </p>
              </div>
            )}

            {/* Sub-policy array setting */}
            {shownSubPolicy &&
              shownSubPolicy.type === 'array' &&
              Array.isArray(subPolicyValue) && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Label className="text-sm font-medium">
                      {shownSubPolicy.name}
                    </Label>
                    {showSubPolicyBeta && <BetaBadge />}
                    {shownSubPolicy.description && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground text-xs">
                            &#9432;
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {shownSubPolicy.description}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {usesPatternModal ? (
                    <PatternArrayModalField
                      value={subPolicyValue as StoredPattern[]}
                      onChange={handleSubPolicyChange}
                      disabled={isBusy || mode === PolicyMode.Off}
                      readOnly={isSupportReadOnly}
                      isSupportReadOnly={isSupportReadOnly}
                      policyKey={policyKey}
                      policyName={shownSubPolicy.name}
                      policyDescription={getPatternModalDescription(
                        policyKey,
                        shownSubPolicy.key,
                        shownSubPolicy.description
                      )}
                      baseline={
                        Array.isArray(subPolicyBaseline)
                          ? (subPolicyBaseline as StoredPattern[])
                          : []
                      }
                    />
                  ) : (
                    <ArrayObjectSetting
                      setting={buildSettingFromEditContext(shownSubPolicy)}
                      value={subPolicyValue as Record<string, unknown>[]}
                      onChange={handleSubPolicyChange}
                      disabled={
                        isBusy || mode === PolicyMode.Off || isSupportReadOnly
                      }
                    />
                  )}
                </div>
              )}

            {/* KIR-4929: the Policies page rows that belong with this
                sub-policy, e.g. "Secret Detection Rules" under "Secret
                Rules" (ALERT_SUB_POLICY_RELATED_SETTINGS). */}
            {hasRelatedSettings && relatedSettingsLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {hasRelatedSettings &&
              !isAccountScope &&
              organizationSettingsError && (
                <p className="text-sm text-destructive">
                  Failed to load the rest of this policy&apos;s settings.{' '}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() => void refetchOrganizationPolicySettings()}
                  >
                    Try again
                  </button>
                </p>
              )}
            {hasRelatedRows && (
              <RelatedPolicySettings
                policyKey={policyKey}
                policyName={editContext.policy.name}
                settings={relatedSettings}
                control={relatedForm.control}
                shouldHideSetting={shouldHideSetting}
                readOnly={isBusy || isSupportReadOnly}
                isSupportReadOnly={isSupportReadOnly}
                isDirty={relatedDirty}
                parentMode={mode}
                supportsEnforce={editContext.policy.supportsEnforce ?? false}
                enforceDisabledReason={editContext.policy.enforceDisabledReason}
                hasUnsavedChanges={hasUnsavedChanges}
                confirmDiscard={confirmDiscard}
                extras={relatedExtras}
              />
            )}
          </div>
        )}

        {/* Conflict warning (organization scope only — account is version-less) */}
        {supportsConflict && hasConflict && (
          <p className="text-sm text-destructive">
            This policy was updated from another surface or session. Please
            reload the action before saving.
          </p>
        )}

        {accountSettingsUnavailable && (
          <p className="text-sm text-destructive">
            Failed to load policy settings required to save changes.{' '}
            <button
              type="button"
              className="underline font-medium"
              onClick={() => void refetchAccountPolicySettings()}
            >
              Try again
            </button>
          </p>
        )}

        {/* Footer: Save + Cancel left, Reset to Defaults right */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                !hasUnsavedChanges ||
                isBusy ||
                isLoading ||
                hasConflict ||
                isSupportReadOnly ||
                (pendingSettingsWrite &&
                  (accountSettingsHydrating || accountSettingsUnavailable))
              }
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={closeDialog}
              disabled={isBusy}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>

          {showResetButton && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={isBusy || isSupportReadOnly}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset to Defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Reset {shownSubPolicy?.name}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset &quot;{shownSubPolicy?.name}&quot; back to
                    its default value. Other settings in &quot;
                    {editContext?.policy.name}&quot; are not changed. This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetToDefaults}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Reset Settings
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
